import { useState, useRef, useEffect } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Filter, Search, SlidersHorizontal, Grid3X3, List, ArrowLeft, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useServicesData } from "@/hooks/useServicesData";
import { useExtendedServicesFilters } from "@/hooks/useExtendedServicesFilters";
import { Skeleton } from "@/components/ui/skeleton";
import { useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";
import { Link } from "react-router-dom";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Eye } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";

const MobileMarketplace = () => {
  const [activeTab, setActiveTab] = useState('services');
  const [showFilters, setShowFilters] = useState(false);
  const navigate = useNavigate();
  const servicesScrollRef = useRef<HTMLDivElement>(null);
  const productsScrollRef = useRef<HTMLDivElement>(null);
  const [servicesScrollEnd, setServicesScrollEnd] = useState(false);
  const [servicesScrollStart, setServicesScrollStart] = useState(true);
  const [productsScrollEnd, setProductsScrollEnd] = useState(false);
  const [productsScrollStart, setProductsScrollStart] = useState(true);
  const isMobile = useIsMobile();

  const {
    services,
    products,
    isServicesLoading,
    isProductsLoading,
  } = useServicesData();

  const {
    searchTerm,
    setSearchTerm,
    viewMode,
    setViewMode,
    sortBy,
    setSortBy,
    filteredServices,
    filteredProducts
  } = useExtendedServicesFilters(services, products);

  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
  };

  const handleProductClick = (productId: string, event: React.MouseEvent) => {
    // Prévenir le comportement par défaut et la propagation
    event.preventDefault();
    event.stopPropagation();
    
    console.log("ProductGrid handleProductClick - Product clicked:", productId);
    
    // Déterminer la bonne route en fonction de l'appareil
    const route = isMobile 
      ? `/product-detail-mobile/${productId}` 
      : `/services/products/${productId}`;
    
    console.log("Navigating to route:", route);
    navigate(route);
  };

  const handleServiceClick = (serviceId: string, event: React.MouseEvent) => {
    event.preventDefault();
    const route = `/service-detail-mobile/${serviceId}`;
    console.log("MobileMarketplace - Navigating to service detail:", route);
    navigate(route);
  };

  useEffect(() => {
    const checkServicesScrollPosition = () => {
      if (!servicesScrollRef.current) return;
      
      const { scrollLeft, scrollWidth, clientWidth } = servicesScrollRef.current;
      setServicesScrollStart(scrollLeft <= 10);
      setServicesScrollEnd(scrollWidth - scrollLeft - clientWidth <= 10);
    };

    const checkProductsScrollPosition = () => {
      if (!productsScrollRef.current) return;
      
      const { scrollLeft, scrollWidth, clientWidth } = productsScrollRef.current;
      setProductsScrollStart(scrollLeft <= 10);
      setProductsScrollEnd(scrollWidth - scrollLeft - clientWidth <= 10);
    };

    const servicesContainer = servicesScrollRef.current;
    const productsContainer = productsScrollRef.current;
    
    if (servicesContainer) {
      servicesContainer.addEventListener('scroll', checkServicesScrollPosition);
      // Initialize scroll position check
      checkServicesScrollPosition();
    }
    
    if (productsContainer) {
      productsContainer.addEventListener('scroll', checkProductsScrollPosition);
      // Initialize scroll position check
      checkProductsScrollPosition();
    }
    
    return () => {
      if (servicesContainer) {
        servicesContainer.removeEventListener('scroll', checkServicesScrollPosition);
      }
      if (productsContainer) {
        productsContainer.removeEventListener('scroll', checkProductsScrollPosition);
      }
    };
  }, [filteredServices?.length, filteredProducts?.length]);

  const handleServicesScrollLeft = () => {
    if (servicesScrollRef.current) {
      const { scrollLeft } = servicesScrollRef.current;
      const cardWidth = servicesScrollRef.current.querySelector('.service-card')?.clientWidth || 0;
      const scrollAmount = Math.max(0, scrollLeft - cardWidth * 2);
      
      servicesScrollRef.current.scrollTo({
        left: scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  const handleServicesScrollRight = () => {
    if (servicesScrollRef.current) {
      const { scrollLeft } = servicesScrollRef.current;
      const cardWidth = servicesScrollRef.current.querySelector('.service-card')?.clientWidth || 0;
      
      servicesScrollRef.current.scrollTo({
        left: scrollLeft + cardWidth * 2,
        behavior: 'smooth'
      });
    }
  };

  const handleProductsScrollLeft = () => {
    if (productsScrollRef.current) {
      const { scrollLeft } = productsScrollRef.current;
      const cardWidth = productsScrollRef.current.querySelector('.product-card')?.clientWidth || 0;
      const scrollAmount = Math.max(0, scrollLeft - cardWidth * 2);
      
      productsScrollRef.current.scrollTo({
        left: scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  const handleProductsScrollRight = () => {
    if (productsScrollRef.current) {
      const { scrollLeft } = productsScrollRef.current;
      const cardWidth = productsScrollRef.current.querySelector('.product-card')?.clientWidth || 0;
      
      productsScrollRef.current.scrollTo({
        left: scrollLeft + cardWidth * 2,
        behavior: 'smooth'
      });
    }
  };

  // Function for formatting price with the correct currency
  const formatPrice = (price: number, currency: string = 'EUR') => {
    const currencySymbol = getCurrencySymbol(currency);
    return `${price.toFixed(2)} ${currencySymbol}`;
  };
  
  // Function to get currency symbol
  const getCurrencySymbol = (currencyCode: string = 'EUR') => {
    switch (currencyCode) {
      case "EUR": return "€";
      case "USD": return "$";
      case "HTG": return "G";
      case "DOP": return "RD$";
      case "GBP": return "£";
      case "CAD": return "CA$";
      case "CHF": return "Fr";
      case "AUD": return "A$";
      case "JPY": return "¥";
      case "CNY": return "¥";
      case "RUB": return "₽";
      case "BRL": return "R$";
      case "MXN": return "Mex$";
      case "INR": return "₹";
      case "MAD": return "DH";
      case "XAF": return "FCFA";
      case "XOF": return "CFA";
      case "NGN": return "₦";
      case "ZAR": return "R";
      case "KES": return "KSh";
      default: return currencyCode;
    }
  };

  return (
    <div className="pb-20">
      {/* Header */}
      <div className="bg-white p-4 sticky top-0 z-10 shadow-sm">
        <div className="flex items-center gap-2 mb-3">
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={() => navigate(-1)}
            aria-label="Retour"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-xl font-semibold">Marketplace</h1>
        </div>
        
        {/* Search bar */}
        <div className="relative mb-3">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Rechercher..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9 h-9 text-sm"
          />
        </div>
        
        {/* Filters and view toggle */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Button 
              size="sm" 
              variant="outline" 
              className="h-8 text-xs flex gap-1 items-center"
              onClick={() => setShowFilters(!showFilters)}
            >
              <Filter className="h-3.5 w-3.5" />
              Filtres
            </Button>
            <Button 
              size="sm" 
              variant="outline" 
              className="h-8 text-xs flex gap-1 items-center"
            >
              <SlidersHorizontal className="h-3.5 w-3.5" />
              Trier
            </Button>
          </div>
          <div className="flex items-center border rounded-md overflow-hidden h-8">
            <Button 
              variant={viewMode === "grid" ? "default" : "ghost"} 
              size="sm"
              className="h-8 rounded-none px-2"
              onClick={() => setViewMode("grid")}
            >
              <Grid3X3 className="h-3.5 w-3.5" />
            </Button>
            <Button 
              variant={viewMode === "list" ? "default" : "ghost"} 
              size="sm"
              className="h-8 rounded-none px-2"
              onClick={() => setViewMode("list")}
            >
              <List className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="px-4 pt-3">
        <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
          <TabsList className="grid grid-cols-2 w-full mb-4">
            <TabsTrigger value="services">Services</TabsTrigger>
            <TabsTrigger value="products">Produits</TabsTrigger>
          </TabsList>

          {/* Services content */}
          <TabsContent value="services" className="mt-2">
            {isServicesLoading ? (
              <div className="grid grid-cols-2 gap-3">
                {Array.from({ length: 4 }).map((_, i) => (
                  <Skeleton key={i} className="aspect-[3/4] rounded-lg" />
                ))}
              </div>
            ) : (
              <>
                <div className="flex justify-between items-center mb-3">
                  <h3 className="font-semibold text-base">Services disponibles</h3>
                  <div className="flex gap-1">
                    <Button 
                      onClick={handleServicesScrollLeft} 
                      variant="ghost" 
                      size="icon" 
                      className={cn(
                        "h-7 w-7 rounded-full bg-gray-100 hover:bg-gray-200 transition-opacity", 
                        servicesScrollStart ? "opacity-50 cursor-not-allowed" : "opacity-100"
                      )}
                      disabled={servicesScrollStart}
                      aria-label="Défiler à gauche"
                    >
                      <ChevronLeft className="h-5 w-5" />
                    </Button>
                    <Button 
                      onClick={handleServicesScrollRight} 
                      variant="ghost" 
                      size="icon"
                      className={cn(
                        "h-7 w-7 rounded-full bg-gray-100 hover:bg-gray-200 transition-opacity",
                        servicesScrollEnd ? "opacity-50 cursor-not-allowed" : "opacity-100"
                      )}
                      disabled={servicesScrollEnd}
                      aria-label="Défiler à droite"
                    >
                      <ChevronRight className="h-5 w-5" />
                    </Button>
                  </div>
                </div>
                
                {filteredServices && filteredServices.length > 0 ? (
                  <div
                    ref={servicesScrollRef}
                    className="flex gap-3 pb-4 overflow-x-auto scrollbar-hide snap-x snap-mandatory scroll-smooth"
                    style={{ 
                      WebkitOverflowScrolling: 'touch',
                      scrollbarWidth: 'none', 
                      msOverflowStyle: 'none'
                    }}
                  >
                    {filteredServices.map((service) => (
                      <div 
                        key={service.id}
                        className="service-card flex-shrink-0 w-[160px] rounded-lg overflow-hidden shadow-sm border hover:shadow-md transition-shadow snap-start cursor-pointer"
                        onClick={(e) => handleServiceClick(service.id, e)}
                      >
                        <div className="aspect-[4/3] w-full bg-gray-100 relative">
                          {service.photo_url ? (
                            <img 
                              src={service.photo_url} 
                              alt={service.service_name} 
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center bg-gray-50">
                              <span className="text-gray-400 text-xs">Pas d'image</span>
                            </div>
                          )}
                          <div className="absolute top-2 right-2 bg-background/95 px-2 py-1 rounded-full shadow-sm backdrop-blur-sm">
                            <span className="text-xs font-medium">
                              {formatPrice(service.price, service.currency)}
                            </span>
                          </div>
                        </div>
                        
                        <div className="p-2 space-y-1">
                          <h4 className="font-medium text-xs line-clamp-1">{service.service_name}</h4>
                          <div className="flex items-center gap-1 text-xs text-muted-foreground">
                            <span>{service.duration} min</span>
                          </div>
                          <div className="flex items-center gap-1 pt-1 border-t mt-1">
                            <Avatar className="h-4 w-4">
                              <AvatarFallback className="text-[8px]">
                                {service.profiles?.full_name?.charAt(0) || 'P'}
                              </AvatarFallback>
                            </Avatar>
                            <span className="text-[10px] text-muted-foreground truncate">
                              {service.profiles?.full_name || 'Professionnel'}
                            </span>
                          </div>
                          
                          <Button 
                            variant="outline" 
                            size="sm" 
                            className="w-full mt-1 h-7 text-[10px] border-primary text-primary hover:bg-primary/5"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleServiceClick(service.id, e);
                            }}
                          >
                            <Eye className="mr-1 h-3 w-3" /> Voir le service
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="bg-gray-100 p-4 rounded-lg text-center text-sm text-gray-500">
                    Aucun service disponible avec ces critères
                  </div>
                )}
              </>
            )}
          </TabsContent>

          {/* Products content */}
          <TabsContent value="products" className="mt-2">
            {isProductsLoading ? (
              <div className="grid grid-cols-2 gap-3">
                {Array.from({ length: 4 }).map((_, i) => (
                  <Skeleton key={i} className="aspect-[3/4] rounded-lg" />
                ))}
              </div>
            ) : (
              <>
                <div className="flex justify-between items-center mb-3">
                  <h3 className="font-semibold text-base">Produits disponibles</h3>
                  <div className="flex gap-1">
                    <Button 
                      onClick={handleProductsScrollLeft} 
                      variant="ghost" 
                      size="icon" 
                      className={cn(
                        "h-7 w-7 rounded-full bg-gray-100 hover:bg-gray-200 transition-opacity", 
                        productsScrollStart ? "opacity-50 cursor-not-allowed" : "opacity-100"
                      )}
                      disabled={productsScrollStart}
                      aria-label="Défiler à gauche"
                    >
                      <ChevronLeft className="h-5 w-5" />
                    </Button>
                    <Button 
                      onClick={handleProductsScrollRight} 
                      variant="ghost" 
                      size="icon"
                      className={cn(
                        "h-7 w-7 rounded-full bg-gray-100 hover:bg-gray-200 transition-opacity",
                        productsScrollEnd ? "opacity-50 cursor-not-allowed" : "opacity-100"
                      )}
                      disabled={productsScrollEnd}
                      aria-label="Défiler à droite"
                    >
                      <ChevronRight className="h-5 w-5" />
                    </Button>
                  </div>
                </div>
                
                {filteredProducts && filteredProducts.length > 0 ? (
                  <div
                    ref={productsScrollRef}
                    className="flex gap-3 pb-4 overflow-x-auto scrollbar-hide snap-x snap-mandatory scroll-smooth"
                    style={{ 
                      WebkitOverflowScrolling: 'touch',
                      scrollbarWidth: 'none', 
                      msOverflowStyle: 'none'
                    }}
                  >
                    {filteredProducts.map((product) => (
                      <div 
                        key={product.id}
                        className="product-card flex-shrink-0 w-[160px] rounded-lg overflow-hidden shadow-sm border hover:shadow-md transition-shadow snap-start cursor-pointer"
                        onClick={(e) => handleProductClick(product.id, e)}
                      >
                        <div className="aspect-[4/3] w-full bg-gray-100 relative">
                          {product.photo_url ? (
                            <img 
                              src={product.photo_url} 
                              alt={product.title} 
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center bg-gray-50">
                              <span className="text-gray-400 text-xs">Pas d'image</span>
                            </div>
                          )}
                          <div className="absolute top-2 right-2 bg-green-500 px-2 py-1 rounded-full shadow-sm">
                            <span className="text-xs font-medium text-white">
                              {formatPrice(product.price, product.currency)}
                            </span>
                          </div>
                        </div>
                        
                        <div className="p-2 space-y-1">
                          <h4 className="font-medium text-xs line-clamp-1">{product.title}</h4>
                          <div className="flex items-center gap-1 pt-1 border-t mt-1">
                            <Avatar className="h-4 w-4">
                              <AvatarFallback className="text-[8px]">
                                {product.profiles?.full_name?.charAt(0) || 'V'}
                              </AvatarFallback>
                            </Avatar>
                            <span className="text-[10px] text-muted-foreground truncate">
                              {product.profiles?.full_name || 'Vendeur'}
                            </span>
                          </div>
                          
                          <Button 
                            variant="outline" 
                            size="sm" 
                            className="w-full mt-1 h-7 text-[10px] border-primary text-primary hover:bg-primary/5"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleProductClick(product.id, e);
                            }}
                          >
                            <Eye className="mr-1 h-3 w-3" /> Voir le produit
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="bg-gray-100 p-4 rounded-lg text-center text-sm text-gray-500">
                    Aucun produit disponible avec ces critères
                  </div>
                )}
              </>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default MobileMarketplace;
